/**
 * Customer CRM API Schemas
 *
 * Zod schemas for the Customers API domain.
 * Covers listing, detail, update, merge, notes, tags, import/export, timeline, and analytics.
 */

import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { ApiSuccessSchema } from "../../../common/response.schema";

// =============================================================================
// Response Schemas
// =============================================================================

/**
 * Customer response — what the frontend receives in list responses
 */
export const CustomerResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  externalUserId: z.string().nullable(),
  displayName: z.string().nullable(),
  primaryEmail: z.string().nullable(),
  primaryPhone: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  language: z.string().nullable(),
  timezone: z.string().nullable(),
  tags: z.array(z.string()),
  customFields: z.record(z.string(), z.unknown()),
  totalSessions: z.number().int(),
  totalMessages: z.number().int(),
  firstSeenAt: z.string().datetime(),
  lastSeenAt: z.string().datetime(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime().nullable()
});

export type CustomerResponse = z.infer<typeof CustomerResponseSchema>;

/**
 * Customer identity response — linked identity for a customer
 */
export const CustomerIdentityResponseSchema = z.object({
  id: z.string().uuid(),
  identifier: z.string(),
  service: z.nativeEnum(DbEnum.DeploymentType),
  isPrimary: z.boolean(),
  verifiedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime()
});

export type CustomerIdentityResponse = z.infer<typeof CustomerIdentityResponseSchema>;

/**
 * Session summary — lightweight session info for detail page
 *
 * `deploymentId` is nullable because `DeploymentSession.deploymentId` is: the
 * FK is `ON DELETE SET NULL`, so deleting a Deployment orphans its sessions
 * rather than removing them, and the history stays attributed to the customer.
 * The sibling `deploymentName` / `deploymentType` were already nullable for the
 * same reason — the id was simply missed.
 */
export const SessionSummarySchema = z.object({
  id: z.string().uuid(),
  deploymentId: z.string().uuid().nullable(),
  deploymentName: z.string().nullable(),
  deploymentType: z.nativeEnum(DbEnum.DeploymentType).nullable(),
  createdAt: z.string().datetime(),
  messageCount: z.number().int(),
  chatNanoId: z.string().nullable()
});

export type SessionSummary = z.infer<typeof SessionSummarySchema>;

/**
 * Note — internal team annotation on a customer
 */
export const CustomerNoteSchema = z.object({
  id: z.string().uuid(),
  content: z.string(),
  userId: z.string(),
  userName: z.string().nullable(),
  createdAt: z.string().datetime()
});

export type CustomerNote = z.infer<typeof CustomerNoteSchema>;

/**
 * Audit log entry — tracks CRM actions on a customer
 */
export const CustomerAuditEntrySchema = z.object({
  action: z.string(),
  userId: z.string().nullable(),
  details: z.record(z.string(), z.unknown()).optional(),
  timestamp: z.string().datetime()
});

export type CustomerAuditEntry = z.infer<typeof CustomerAuditEntrySchema>;

/**
 * Customer detail response — full customer profile with related data
 */
export const CustomerDetailResponseSchema = CustomerResponseSchema.extend({
  identities: z.array(CustomerIdentityResponseSchema),
  recentSessions: z.array(SessionSummarySchema),
  notes: z.array(CustomerNoteSchema),
  auditLog: z.array(CustomerAuditEntrySchema)
});

export type CustomerDetailResponse = z.infer<typeof CustomerDetailResponseSchema>;

/**
 * Timeline entry — chronological activity event
 */
export const CustomerTimelineEntrySchema = z.object({
  timestamp: z.string().datetime(),
  type: z.enum([
    "session_started",
    "message_received",
    "tag_added",
    "tag_removed",
    "note_added",
    "merged",
    "updated",
    "identity_linked"
  ]),
  channel: z.nativeEnum(DbEnum.DeploymentType).nullable(),
  details: z.record(z.string(), z.unknown()).optional()
});

export type CustomerTimelineEntry = z.infer<typeof CustomerTimelineEntrySchema>;

/**
 * Message search result — a message matching the search query
 */
export const CustomerMessageSearchResultSchema = z.object({
  messageId: z.string(),
  content: z.string(),
  role: z.string(),
  chatNanoId: z.string().nullable(),
  deploymentType: z.nativeEnum(DbEnum.DeploymentType).nullable(),
  createdAt: z.string().datetime()
});

export type CustomerMessageSearchResult = z.infer<typeof CustomerMessageSearchResultSchema>;

/**
 * Related customer — another customer with same email domain
 */
export const RelatedCustomerSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string().nullable(),
  primaryEmail: z.string().nullable(),
  totalSessions: z.number().int(),
  lastSeenAt: z.string().datetime()
});

export type RelatedCustomer = z.infer<typeof RelatedCustomerSchema>;

/**
 * Analytics data point — message volume per day per channel
 *
 * `channel` is nullable because a session's deployment can be gone: the
 * `DeploymentSession.deploymentId` FK is `ON DELETE SET NULL`. Absence is
 * expressed as `null`, matching `CustomerTimelineEntrySchema.channel` above —
 * not as a sentinel member of the enum. `DbEnum.DeploymentType` is generated
 * from the Prisma schema, so a hand-added `"UNKNOWN"` would be erased on the
 * next `pnpm generate:enums`.
 */
export const CustomerAnalyticsDataPointSchema = z.object({
  date: z.string(),
  channel: z.nativeEnum(DbEnum.DeploymentType).nullable(),
  messageCount: z.number().int()
});

export type CustomerAnalyticsDataPoint = z.infer<typeof CustomerAnalyticsDataPointSchema>;

/**
 * Import result — summary of a CSV import operation
 */
export const ImportResultSchema = z.object({
  created: z.number().int(),
  updated: z.number().int(),
  skipped: z.number().int(),
  errors: z.array(
    z.object({
      row: z.number().int(),
      field: z.string().nullable(),
      message: z.string()
    })
  )
});

export type ImportResult = z.infer<typeof ImportResultSchema>;

// =============================================================================
// Query / Body Schemas
// =============================================================================

/**
 * A single filter condition
 */
export const CustomerFilterSchema = z.object({
  field: z.string().min(1),
  operator: z.string().min(1),
  value: z.unknown().optional()
});

export type CustomerFilter = z.infer<typeof CustomerFilterSchema>;

/**
 * List customers query parameters — supports structured filters
 */
/**
 * The columns a customer list may be ordered by, and the directions.
 *
 * Exported as arrays rather than inlined into `z.enum(...)` because the Prisma
 * adapter needs the same set at runtime to build `orderBy`. It carried its own
 * hand-copied `ALLOWED_SORT_BY` literal, which is a second definition of a
 * security-shaped allowlist and drifts the moment either side gains a column.
 */
export const CUSTOMER_SORT_FIELDS = [
  "lastSeenAt",
  "totalMessages",
  "createdAt",
  "displayName",
  "totalSessions",
  // Offered by the Contacts SortPanel and absent from this list until now, so
  // choosing Email or First Seen silently fell back to `lastSeenAt desc` — the
  // adapter FILTERED an unrecognised rule out rather than refusing it. Both are
  // real `Customer` columns; the omission was the bug, not the sort.
  "primaryEmail",
  "firstSeenAt"
] as const;

export const CUSTOMER_SORT_DIRECTIONS = ["asc", "desc"] as const;

export const ListCustomersQuerySchema = z.object({
  search: z.string().optional(),
  sortBy: z.enum(CUSTOMER_SORT_FIELDS).default("lastSeenAt"),
  sortOrder: z.enum(CUSTOMER_SORT_DIRECTIONS).default("desc"),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  /** Legacy simple filters (still supported) */
  channel: z.nativeEnum(DbEnum.DeploymentType).optional(),
  tag: z.string().optional(),
  /** Structured filters — JSON-encoded array of CustomerFilter or FilterGroup. Parsed by the controller. */
  filters: z.string().optional(),
  /** Multi-sort — JSON-encoded array of SortRule. Takes precedence over sortBy/sortOrder when present. */
  sorts: z.string().optional(),
  /**
   * Include customers whose every identity is an anonymous, coined one.
   *
   * Excluded by default. Such a row carries no name, email, phone or external
   * id — there is nothing to display and no way to make contact — and every
   * deployment session mints one, so in a contact directory they are noise
   * rather than data. Explicit opt-in keeps them reachable for an integrator
   * reconciling raw rows, and makes the sane view the one you get by saying
   * nothing.
   *
   * `z.coerce.boolean()` is unsafe for a query string — `Boolean("false") ===
   * true` — so `?includeAnonymous=false` would mean its own opposite and any
   * typo would read as `true`. Preprocessed explicitly, matching
   * `DeleteOrganizationQuerySchema` and `PipedreamBackfillCategoriesParamsSchema`:
   * only the exact strings "true"/"false" (or a real boolean, for an internal
   * caller) are accepted, and anything else is a 400 rather than a guess.
   *
   * 🔴 `.optional()` and NOT `.default(false)`, unlike `page` and `limit` beside
   * it. A `.default()` is required on the schema's OUTPUT type, so every caller
   * building this query object has to name the field — and the Contacts list is
   * precisely the caller that must get the sane view by saying NOTHING. Writing
   * `includeAnonymous: false` there would also pin the dashboard to a literal,
   * so the one caller that most needs to follow the default would be the one
   * that stopped following it. Omitted means excluded, and
   * `ListCustomersPrismaAdapter` is the single place that resolves it.
   *
   * 🔴 THE `.optional()` SITS OUTSIDE THE `preprocess`, AND THE TWO POSITIONS
   * PARSE IDENTICALLY. `z.preprocess(fn, z.boolean().optional())` accepts an
   * omitted value exactly as this does — so every parse-level test passes either
   * way, and nothing about reading the schema says which you are holding. What
   * differs is the PUBLISHED contract: `z.preprocess` builds a pipe whose input
   * side carries no optionality of its own, so with the `.optional()` on the
   * INNER schema `z.toJSONSchema(…, { io: "input" })` lists the field in
   * `required`. `customers_list` then advertises a mandatory parameter whose
   * whole purpose is to be omitted. Outside, the field is a `ZodOptional` and
   * the emitter reads it as optional.
   *
   * The two surfaces are different denominators — what the HTTP parse ACCEPTS
   * and what the tool catalog ADVERTISES — and only the second one moved.
   * `customer-list-include-anonymous.test.ts` pins both.
   */
  includeAnonymous: z
    .preprocess((v) => (v === "true" ? true : v === "false" ? false : v), z.boolean())
    .optional()
});

export type ListCustomersQuery = z.infer<typeof ListCustomersQuerySchema>;

/**
 * Lightweight customer row for filter pickers / mention lists / any UI that
 * needs to render a customer chip without the heavyweight `customFields`,
 * `totalSessions`, `tags` etc. that the full `CustomerResponseSchema`
 * carries. Five fields = the minimum to render a row with avatar + label +
 * subtitle.
 */
export const CustomerPickerRowSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string().nullable(),
  primaryEmail: z.string().nullable(),
  primaryPhone: z.string().nullable(),
  avatarUrl: z.string().nullable()
});

export type CustomerPickerRow = z.infer<typeof CustomerPickerRowSchema>;

/**
 * Query parameters for the picker-options endpoint.
 *
 * Either `ids` (hydrate a known selection — top-N lookup by primary key)
 * or `search` (keystroke-search the directory) drives the result set.
 * Supplying both AND-merges: "customers whose id is in {ids} AND whose
 * name/email/phone matches {search}". Supplying neither returns the
 * most-recently-active `limit` customers — the same shape a fresh open
 * of the picker would see.
 *
 * 🔴 **EVERY PATH SUBTRACTS THE ANONYMOUS PLACEHOLDERS, AND THERE IS NO
 * `includeAnonymous` HERE.** The picker reads the same directory Contacts does,
 * so it hides the same rows: a customer with no name, no email, no phone and no
 * external id has nothing to render but the coined `anon:` identifier, on the
 * hydration path as much as on the search path. `ListCustomersQuerySchema` offers
 * the opt-in because an integrator reconciling against `GET /public/v1/customers`
 * has a use for those rows; a picker control does not.
 */
export const CustomerPickerOptionsQuerySchema = z.object({
  /**
   * Comma-separated UUID list. Used by the hydration path to fetch a
   * known selection in one round-trip — `?ids=u1,u2,u3`. Capped at 200
   * IDs per request to bound the worst-case payload. Empty / whitespace
   * entries are dropped before UUID validation.
   */
  ids: z
    .preprocess(
      (val) =>
        typeof val === "string"
          ? val
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : val,
      z.array(z.string().uuid()).max(200).optional()
    )
    .optional(),
  search: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(25)
});

export type CustomerPickerOptionsQuery = z.infer<typeof CustomerPickerOptionsQuerySchema>;

/**
 * Path variables for single-customer operations
 */
export const CustomerPathVarsSchema = z.object({
  id: z.string().uuid()
});

/**
 * Path variables for the lookup by the business's own user identifier
 * (`GET /public/v1/customers/by-external-id/:externalUserId`).
 *
 * `.trim().min(1)` matches its two siblings below — the create and update
 * bodies both normalise this field the same way, and a lookup key that accepts
 * what the write path rejects can only ever miss.
 *
 * The trim is the part that does work here. An empty path segment cannot reach
 * the handler at all (Express matches `:param` as `[^/]+?`, so
 * `/by-external-id/` is a 404 at routing), but `%20` and `%09` can: they arrive
 * as `" "` and `"\t"`, pass a bare `.min(1)`, and then
 * `FindCustomerByExternalUserIdPrismaAdapter` trims them to empty and returns
 * `null` — a 200 with `data: null` that reads as "no such customer" rather than
 * "you sent a blank identifier". Trimming at the boundary turns that into a 400
 * that names the field.
 *
 * No maximum and no format, deliberately. `Customer.externalUserId` is `String?`
 * in Prisma — uncapped `text` — and while most write paths cap at 255, the CSV
 * import path applies no cap, so longer values genuinely exist. A lookup key
 * must express every value the column can hold, or a stored customer becomes
 * unreachable while the row sits there.
 */
export const CustomerExternalUserIdPathVarsSchema = z.object({
  externalUserId: z.string().trim().min(1)
});

/**
 * Create customer body
 */
export const CreateCustomerBodySchema = z.object({
  displayName: z.string().trim().min(1).max(200),
  externalUserId: z.string().trim().min(1).max(255).optional(),
  primaryEmail: z.string().email().optional(),
  primaryPhone: z.string().min(1).max(20).optional(),
  tags: z.array(z.string().trim().min(1).max(50)).optional(),
  customFields: z.record(z.string(), z.unknown()).optional()
});

export type CreateCustomerBody = z.infer<typeof CreateCustomerBodySchema>;

/**
 * Update customer body
 */
export const UpdateCustomerBodySchema = z.object({
  displayName: z.string().trim().min(1).max(200).optional(),
  externalUserId: z.string().trim().min(1).max(255).nullable().optional(),
  primaryEmail: z.string().trim().email().max(255).nullable().optional(),
  primaryPhone: z.string().trim().max(30).nullable().optional(),
  avatarUrl: z.string().trim().url().nullable().optional(),
  tags: z.array(z.string().trim().min(1).max(50)).optional(),
  customFields: z.record(z.string(), z.unknown()).optional()
});

export type UpdateCustomerBody = z.infer<typeof UpdateCustomerBodySchema>;

/**
 * Link session body — attach an existing session to this customer
 */
export const LinkSessionBodySchema = z.object({
  sessionId: z.string().uuid()
});

export type LinkSessionBody = z.infer<typeof LinkSessionBodySchema>;

/**
 * Unlinked session — a session with no customer, available for linking
 *
 * `deploymentId` is nullable for the same reason as `SessionSummarySchema`:
 * the FK is `ON DELETE SET NULL`. This schema is the one that fires in
 * practice — the endpoint selects exactly `customerId IS NULL`, and an
 * orphaned session is never linked to a customer.
 */
export const UnlinkedSessionSchema = z.object({
  id: z.string().uuid(),
  deploymentId: z.string().uuid().nullable(),
  deploymentName: z.string().nullable(),
  deploymentType: z.nativeEnum(DbEnum.DeploymentType).nullable(),
  createdAt: z.string().datetime(),
  messageCount: z.number().int(),
  chatNanoId: z.string().nullable(),
  firstMessagePreview: z.string().nullable()
});

export type UnlinkedSession = z.infer<typeof UnlinkedSessionSchema>;

/**
 * Query params for listing unlinked sessions
 */
export const UnlinkedSessionsQuerySchema = z.object({
  search: z.string().optional(),
  channel: z.nativeEnum(DbEnum.DeploymentType).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(20)
});

/**
 * Add note body
 */
export const AddNoteBodySchema = z.object({
  content: z.string().trim().min(1).max(5000)
});

export type AddNoteBody = z.infer<typeof AddNoteBodySchema>;

/**
 * Update note body
 */
export const UpdateNoteBodySchema = z.object({
  content: z.string().trim().min(1).max(5000)
});

export type UpdateNoteBody = z.infer<typeof UpdateNoteBodySchema>;

/**
 * Note path variables (customer + note)
 */
export const NotePathVarsSchema = z.object({
  id: z.string().uuid(),
  noteId: z.string().uuid()
});

/**
 * Merge customer body — merge loserId into the winner (URL param)
 */
export const MergeCustomerBodySchema = z.object({
  loserId: z.string().uuid()
});

export type MergeCustomerBody = z.infer<typeof MergeCustomerBodySchema>;

/**
 * Bulk tag body — add or remove tags from multiple customers
 */
export const BulkTagBodySchema = z.object({
  customerIds: z.array(z.string().uuid()).min(1).max(100),
  tags: z.array(z.string().trim().min(1).max(50)).min(1),
  action: z.enum(["add", "remove"])
});

export type BulkTagBody = z.infer<typeof BulkTagBodySchema>;

/**
 * Timeline query parameters
 */
export const CustomerTimelineQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(50)
});

/**
 * Message search query parameters
 *
 * `query` is trimmed BEFORE its length is checked. A bare `.min(1)` admits a
 * whitespace-only term, which reaches Prisma `contains` and matches every
 * message containing a space — returning the customer's whole message history
 * instead of a search result. Trimming first makes that input fail loudly with
 * a 400 rather than succeeding with the wrong answer.
 */
export const CustomerSearchMessagesQuerySchema = z.object({
  query: z.string().trim().min(1).max(500),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(20)
});

/**
 * Analytics query parameters
 */
export const CustomerAnalyticsQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(365).default(30)
});

// =============================================================================
// Custom Field Definitions (org-level settings)
// =============================================================================

/**
 * Supported custom field types — inspired by Airtable's field model.
 *
 * Storage: all values live in Customer.metadata.customFields as JSON.
 * The type determines rendering (input widget) and validation (format checks).
 *
 * | Type        | Storage    | Widget              | Example value          |
 * |-------------|------------|---------------------|------------------------|
 * | text        | string     | single-line input   | "Acme Inc"             |
 * | longtext    | string     | textarea            | "Notes about..."       |
 * | number      | number     | numeric input       | 42                     |
 * | currency    | number     | numeric + symbol     | 50000 (+ "USD")        |
 * | percent     | number     | numeric + %          | 85                     |
 * | email       | string     | email input          | "john@acme.com"        |
 * | phone       | string     | tel input            | "+1234567890"          |
 * | url         | string     | url input            | "https://acme.com"     |
 * | date        | string     | date picker          | "2026-12-01"           |
 * | select      | string     | dropdown             | "enterprise"           |
 * | multiselect | string[]   | multi-select chips   | ["vip", "partner"]     |
 * | checkbox    | boolean    | toggle               | true                   |
 * | rating      | number     | star rating (1-5)    | 4                      |
 */
export const CUSTOMER_FIELD_TYPES = [
  "text",
  "longtext",
  "number",
  "currency",
  "percent",
  "email",
  "phone",
  "url",
  "date",
  "select",
  "multiselect",
  "checkbox",
  "rating"
] as const;

export type CustomerFieldType = (typeof CUSTOMER_FIELD_TYPES)[number];

/**
 * A single custom field definition configured by org admin
 */
export const CustomerFieldDefinitionSchema = z.object({
  key: z
    .string()
    .regex(/^[a-z][a-z0-9_]*$/, "Key must be lowercase snake_case")
    .max(50),
  label: z.string().trim().min(1).max(100),
  type: z.enum(CUSTOMER_FIELD_TYPES),
  required: z.boolean().default(false),
  /** Options for select / multiselect types */
  options: z.array(z.string().min(1).max(100)).optional(),
  /** Currency code for currency type (e.g. "USD", "EUR") */
  currencyCode: z.string().length(3).optional()
});

export type CustomerFieldDefinition = z.infer<typeof CustomerFieldDefinitionSchema>;

/**
 * Body for updating field definitions — full replace
 */
export const UpdateCustomerFieldSettingsBodySchema = z.object({
  definitions: z
    .array(CustomerFieldDefinitionSchema)
    .max(20, "Maximum 20 custom fields per organization")
    .refine(
      (defs) => {
        const keys = defs.map((d) => d.key);
        return new Set(keys).size === keys.length;
      },
      { message: "Field keys must be unique" }
    )
    .refine(
      (defs) =>
        defs.every(
          (d) =>
            (d.type !== "select" && d.type !== "multiselect") || (d.options && d.options.length > 0)
        ),
      { message: "Select and multiselect fields must have at least one option" }
    )
    .refine(
      (defs) =>
        defs.every((d) => d.type !== "currency" || (d.currencyCode && d.currencyCode.length === 3)),
      { message: "Currency fields must specify a 3-letter currency code" }
    )
});

export type UpdateCustomerFieldSettingsBody = z.infer<typeof UpdateCustomerFieldSettingsBodySchema>;

// =============================================================================
// Wrapped Response Schemas (ApiSuccessSchema wrapper)
// =============================================================================

export const CustomerCreateResponseSchema = ApiSuccessSchema(CustomerResponseSchema);
export const CustomerListResponseSchema = ApiSuccessSchema(z.array(CustomerResponseSchema));
export const CustomerPickerOptionsResponseSchema = ApiSuccessSchema(
  z.array(CustomerPickerRowSchema)
);
export const CustomerDetailWrappedResponseSchema = ApiSuccessSchema(CustomerDetailResponseSchema);
export const CustomerUpdateResponseSchema = ApiSuccessSchema(CustomerResponseSchema);
export const CustomerDeleteResponseSchema = ApiSuccessSchema(z.object({ deleted: z.boolean() }));
export const CustomerMergeResponseSchema = ApiSuccessSchema(CustomerDetailResponseSchema);
export const CustomerAddNoteResponseSchema = ApiSuccessSchema(CustomerNoteSchema);
export const CustomerBulkTagResponseSchema = ApiSuccessSchema(
  z.object({ updated: z.number().int() })
);
export const CustomerTimelineResponseSchema = ApiSuccessSchema(
  z.array(CustomerTimelineEntrySchema)
);
export const CustomerSearchMessagesResponseSchema = ApiSuccessSchema(
  z.array(CustomerMessageSearchResultSchema)
);
export const CustomerRelatedResponseSchema = ApiSuccessSchema(z.array(RelatedCustomerSchema));
export const CustomerAnalyticsResponseSchema = ApiSuccessSchema(
  z.array(CustomerAnalyticsDataPointSchema)
);
export const CustomerImportResponseSchema = ApiSuccessSchema(ImportResultSchema);
export const CustomerLinkSessionResponseSchema = ApiSuccessSchema(
  z.object({ linked: z.boolean() })
);
export const UnlinkedSessionsResponseSchema = ApiSuccessSchema(z.array(UnlinkedSessionSchema));
export const CustomerFieldSettingsResponseSchema = ApiSuccessSchema(
  z.array(CustomerFieldDefinitionSchema)
);
export const CustomerUpdateNoteResponseSchema = ApiSuccessSchema(CustomerNoteSchema);
export const CustomerDeleteNoteResponseSchema = ApiSuccessSchema(
  z.object({ deleted: z.boolean() })
);
export const CustomerUploadNoteImageResponseSchema = ApiSuccessSchema(
  z.object({ url: z.string() })
);
